import { AlertTriangle, GitCompare } from 'lucide-react'
import clsx from 'clsx'
import { isFlaggedPlan } from '@/utils/patchStage'
import type { PatchPlan } from '@/types/patching'
import styles from './Patching.module.css'

/** Terraform plan summary: add/change/destroy plus the destructive / scope-expanding flags. */
export function PlanPanel({ plan }: { plan: PatchPlan | null | undefined }) {
  return (
    <section className={styles.section} aria-label="Plan summary" data-testid="plan-panel">
      <h3 className={styles.sectionTitle}>
        <GitCompare size={14} /> Terraform plan
      </h3>
      {!plan ? (
        <p className={styles.muted}>No plan has been produced for this environment yet.</p>
      ) : (
        <div className={styles.stack}>
          {!plan.has_changes ? (
            <p className={styles.muted}>The plan has no infrastructure changes.</p>
          ) : (
            <div className={styles.planCounts}>
              <span className={styles.planCount} data-testid="plan-add">
                <strong>{plan.summary.add}</strong> to add
              </span>
              <span className={styles.planCount} data-testid="plan-change">
                <strong>{plan.summary.change}</strong> to change
              </span>
              <span
                className={clsx(styles.planCount, plan.summary.destroy > 0 && styles.planDestroy)}
                data-testid="plan-destroy"
              >
                <strong>{plan.summary.destroy}</strong> to destroy
              </span>
            </div>
          )}
          {plan.destructive && (
            <div className={clsx(styles.alert, styles.alertError)} data-testid="plan-flag-destructive">
              <AlertTriangle size={16} className={styles.alertIcon} />
              <span><strong>Destructive plan.</strong> It destroys or replaces existing resources.</span>
            </div>
          )}
          {plan.scope_expanding && (
            <div className={clsx(styles.alert, styles.alertWarning)} data-testid="plan-flag-scope-expanding">
              <AlertTriangle size={16} className={styles.alertIcon} />
              <span><strong>Scope-expanding plan.</strong> It adds targets beyond the approved scope.</span>
            </div>
          )}
          {isFlaggedPlan(plan) && (
            <p className={styles.muted}>This plan requires additional review: approvers must explicitly acknowledge it.</p>
          )}
          <div>
            <span className={styles.factLabel}>Plan hash</span>
            <p className={styles.hash} data-testid="plan-hash">{plan.plan_hash ?? '(none — no changes)'}</p>
          </div>
        </div>
      )}
    </section>
  )
}
